import { AccountService } from "@gtmhub/accounts/accountsService";
import { IApiTokenInfo } from "@gtmhub/apiTokens/models";
import { ApiTokensService } from "@gtmhub/apiTokens/services";
import { GtmhubController, IScimSettings, isScimSettingKey, isScimTeamNamingConventionType, ScimTeamNamingConventionType } from "@gtmhub/core";
import { IIndicator } from "@gtmhub/error-handling";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { IComponentOptions, IPromise, IQService } from "angular";

const defaultConfiguration: IScimSettings = {
  teamNamingConvention: "manager",
  autoRenameTeams: false,
  deactivateEmptyTeams: false,
};

const defaultConfigurationHideExternalTeams: IScimSettings = {
  teamNamingConvention: "none",
  autoRenameTeams: false,
  deactivateEmptyTeams: false,
};

export class ScimConfigurationCtrl extends GtmhubController {
  public static $inject = ["$rootScope", "ApiTokensService", "AccountService", "$q", "FeatureToggleV2Service"];

  public initialized = false;
  public apiTokenIssued = false;
  public indicators: {
    loading?: IIndicator;
  } = {};
  public configuration: IScimSettings;

  private readonly settingsKey = "scim";

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private apiTokensService: ApiTokensService,
    private accountService: AccountService,
    private $q: IQService,
    private featureToggleV2Service: FeatureToggleV2Service
  ) {
    super();
  }

  public $onInit(): IPromise<void> {
    if (!this.hasPermission()) {
      return;
    }

    this.indicators.loading = { progress: true };
    this.$rootScope.$on("tokenIssued", () => this.setApiTokenIssued());

    this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.HideExternalTeams).then((hideExternalTeams) => {
      return this.$q.all([this.initializeConfiguration({ hideExternalTeams }), this.setApiTokenIssued()]).then(
        () => {
          this.initialized = true;
          this.resetIndicator("loading");
        },
        (error) => {
          this.initialized = true;
          this.indicators.loading = { error };
        }
      );
    });
  }

  public onChange(params: { key: string; value: ScimTeamNamingConventionType | boolean }): IPromise<void> {
    if (!isScimSettingKey(params.key)) {
      return;
    }

    this.indicators.loading = { progress: true };

    const configuration = { ...this.configuration };
    if (params.key === "teamNamingConvention") {
      if (!isScimTeamNamingConventionType(params.value)) {
        return;
      }
      configuration[params.key] = params.value;
    } else {
      configuration[params.key] = !!params.value;
    }

    if (params.key === "teamNamingConvention") {
      if (params.value === "manager") {
        configuration.autoRenameTeams = false;
      } else if (params.value === "none") {
        configuration.autoRenameTeams = false;
        configuration.deactivateEmptyTeams = false;
      }
    }

    return this.accountService
      .setAccountSetting(this.settingsKey, configuration)
      .then(() => {
        this.configuration = configuration;
        this.resetIndicator("loading");
      })
      .catch((error) => {
        this.indicators.loading = { error };
      });
  }

  public resetIndicator(indicator: keyof ScimConfigurationCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  public hasPermission(): boolean {
    return this.$rootScope.hasPermission("ManageConfiguration");
  }

  private setApiTokenIssued(): IPromise<void> {
    return this.apiTokensService.getApiTokens().then((tokens: ICollection<IApiTokenInfo>) => {
      if (tokens.items.some((t) => t.type === "scim" && t.status === "active")) {
        this.apiTokenIssued = true;
      }
    });
  }

  private initializeConfiguration(args: { hideExternalTeams: boolean }): IPromise<void> {
    const scimAccountSettings = this.accountService.getAccountSetting(this.settingsKey) as IScimSettings;
    const defaultSettings = args.hideExternalTeams ? defaultConfigurationHideExternalTeams : defaultConfiguration;

    if (!scimAccountSettings) {
      this.configuration = defaultSettings;
      return this.accountService.setAccountSetting(this.settingsKey, this.configuration);
    }

    for (const k in scimAccountSettings) {
      if (!(k in defaultSettings)) {
        this.configuration = { ...defaultSettings, ...scimAccountSettings };
        return this.accountService.setAccountSetting(this.settingsKey, this.configuration);
      }
    }

    this.configuration = scimAccountSettings;
  }
}

export const ScimConfigurationComponent: IComponentOptions = {
  controller: ScimConfigurationCtrl,
  templateUrl: require("./scim-configuration.component.jade"),
};
